import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import CloseIcon from "@mui/icons-material/Close";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format } from "date-fns/format";
import { isToday } from "date-fns/isToday";
import { setHours } from "date-fns/setHours";
import { setMinutes } from "date-fns/setMinutes";
import { eachDayOfInterval } from "date-fns/eachDayOfInterval";
import { getAvailableSlots } from "../../apis/bookingApi";
import { extractTimeFromDateString } from "../../hooks/Date";
import {
  type BookingDraft,
  type DateSelectionType,
  type LaneOption,
  calculateDraftSubtotal,
  formatCurrency,
  formatDateToYYYYMMDD,
  formatDuration,
  getHourlyPrice,
  getNumberOfDays,
  getTotalDuration,
  timesOverlap,
} from "../../pages/admin/bookingDraftUtils";

interface AdminBookingDraftFieldsProps {
  draft: BookingDraft;
  index: number;
  isFirst: boolean;
  lanes: LaneOption[];
  discountPercent: number;
  siblingSlots: any[];
  dateLocked: boolean;
  timeLocked: boolean;
  onChange: (updates: Partial<BookingDraft>) => void;
  onToggleSameDay?: (checked: boolean) => void;
  onToggleSameTime?: (checked: boolean) => void;
  onCheckingChange?: (checking: boolean) => void;
}

const generateTimeSlots = (date: Date) => {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (const minute of [0, 30]) {
      if (isToday(date)) {
        if (hour < currentHour || (hour === currentHour && minute < currentMinute)) {
          continue;
        }
      }
      const time = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      const label = format(setMinutes(setHours(new Date(), hour), minute), "h:mm a");
      slots.push({ value: time, label });
    }
  }
  return slots;
};

const AdminBookingDraftFields: React.FC<AdminBookingDraftFieldsProps> = ({
  draft,
  isFirst,
  lanes,
  discountPercent,
  siblingSlots,
  dateLocked,
  timeLocked,
  onChange,
  onToggleSameDay,
  onToggleSameTime,
  onCheckingChange,
}) => {
  const [timeSlots, setTimeSlots] = useState<{ value: string; label: string }[]>([]);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  const {
    selectedLaneId,
    dateSelectionType,
    selectedDate,
    selectedDates,
    dateRange,
    startTime,
    endTime,
    useIndividualTimes,
    individualDateTimes,
    hasOverlap,
  } = draft;

  const referenceDate =
    dateSelectionType === "single"
      ? selectedDate
      : dateSelectionType === "multiple"
        ? selectedDates[0]
        : dateRange.from;

  useEffect(() => {
    if (referenceDate) {
      setTimeSlots(generateTimeSlots(referenceDate));
    } else {
      setTimeSlots([]);
    }
  }, [referenceDate]);

  const endTimeSlots = useMemo(() => {
    if (!startTime || timeSlots.length === 0) return [];
    const [startHours, startMinutes] = startTime.split(":").map(Number);
    const startTimeInMinutes = startHours * 60 + startMinutes;
    return timeSlots.filter((slot) => {
      const [slotHours, slotMinutes] = slot.value.split(":").map(Number);
      return slotHours * 60 + slotMinutes > startTimeInMinutes;
    });
  }, [startTime, timeSlots]);

  const datesToCheck = useMemo(() => {
    switch (dateSelectionType) {
      case "single":
        return selectedDate ? [formatDateToYYYYMMDD(selectedDate)] : [];
      case "multiple":
        return selectedDates.map((date) => formatDateToYYYYMMDD(date));
      case "range":
        if (dateRange.from && dateRange.to) {
          return eachDayOfInterval({ start: dateRange.from, end: dateRange.to }).map(
            (date) => formatDateToYYYYMMDD(date)
          );
        }
        return [];
      default:
        return [];
    }
  }, [dateSelectionType, selectedDate, selectedDates, dateRange]);

  const checkOverlap = useCallback(
    (slots: any[], checkStartTime: string, checkEndTime: string, date?: string) => {
      if (!checkStartTime || !checkEndTime) return false;
      const allSlots = [
        ...slots,
        ...siblingSlots.filter((slot) => !date || slot.date === date || !slot.date),
      ];
      return allSlots.some((slot: any) => {
        const slotStart = extractTimeFromDateString(slot.startTime);
        const slotEnd = extractTimeFromDateString(slot.endTime);
        return timesOverlap(checkStartTime, checkEndTime, slotStart, slotEnd);
      });
    },
    [siblingSlots]
  );

  useEffect(() => {
    let isMounted = true;
    if (!selectedLaneId || datesToCheck.length === 0) {
      onChange({ dateAvailabilities: [], hasOverlap: false });
      onCheckingChange?.(false);
      return;
    }

    const fetchAll = async () => {
      setIsCheckingAvailability(true);
      onCheckingChange?.(true);
      try {
        const availabilities = await Promise.all(
          datesToCheck.map(async (date) => {
            try {
              const response = await getAvailableSlots(date, selectedLaneId);
              const bookedSlots = response.data.bookedSlots || [];
              let overlap = false;
              if (dateSelectionType === "multiple" && useIndividualTimes) {
                const dateTimes = individualDateTimes[date];
                if (dateTimes?.startTime && dateTimes?.endTime) {
                  overlap = checkOverlap(
                    bookedSlots,
                    dateTimes.startTime,
                    dateTimes.endTime,
                    date
                  );
                }
              } else if (startTime && endTime) {
                overlap = checkOverlap(bookedSlots, startTime, endTime, date);
              }
              return {
                date,
                bookedSlots,
                loading: false,
                error: null,
                hasOverlap: overlap,
              };
            } catch {
              return {
                date,
                bookedSlots: [],
                loading: false,
                error: "Unable to load booked slots.",
                hasOverlap: false,
              };
            }
          })
        );
        if (isMounted) {
          onChange({
            dateAvailabilities: availabilities,
            hasOverlap: availabilities.some((item) => item.hasOverlap),
          });
        }
      } finally {
        if (isMounted) {
          setIsCheckingAvailability(false);
          onCheckingChange?.(false);
        }
      }
    };

    fetchAll();
    return () => {
      isMounted = false;
      onCheckingChange?.(false);
    };
    // Intentionally omit onChange to avoid extra fetches from parent identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedLaneId,
    datesToCheck.join(","),
    startTime,
    endTime,
    useIndividualTimes,
    JSON.stringify(individualDateTimes),
    siblingSlots,
    dateSelectionType,
  ]);

  const handleDateSelectionTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newType: string | null
  ) => {
    if (dateLocked || newType === null) return;
    onChange({
      dateSelectionType: newType as DateSelectionType,
      selectedDate: undefined,
      selectedDates: [],
      dateRange: { from: undefined, to: undefined },
      startTime: timeLocked ? startTime : "",
      endTime: timeLocked ? endTime : "",
    });
  };

  const handleSingleDateSelect = (date: Date | undefined) => {
    if (dateLocked) return;
    onChange({
      selectedDate: date,
      startTime: timeLocked ? startTime : "",
      endTime: timeLocked ? endTime : "",
    });
  };

  const handleMultipleDateSelectForPicker = (dates: Date[] | undefined) => {
    if (dateLocked || !dates) return;
    const selectedKeys = new Set(dates.map((date) => formatDateToYYYYMMDD(date)));
    const nextTimes = Object.fromEntries(
      Object.entries(individualDateTimes).filter(([dateKey]) => selectedKeys.has(dateKey))
    );
    onChange({ selectedDates: dates, individualDateTimes: nextTimes });
  };

  const handleDateRangeSelectForPicker = (range: any) => {
    if (dateLocked || !range) return;
    const next: Partial<BookingDraft> = { dateRange: range };
    if (range.from && range.to) {
      next.selectedDates = eachDayOfInterval({ start: range.from, end: range.to });
    }
    onChange(next);
  };

  const handleRemoveDate = (dateToRemove: Date) => {
    if (dateLocked) return;
    const dateKey = formatDateToYYYYMMDD(dateToRemove);
    const nextTimes = { ...individualDateTimes };
    delete nextTimes[dateKey];
    onChange({
      selectedDates: selectedDates.filter((date) => date.getTime() !== dateToRemove.getTime()),
      individualDateTimes: nextTimes,
    });
  };

  const handleStartTimeChange = (event: SelectChangeEvent) => {
    if (timeLocked) return;
    const newStartTime = event.target.value;
    let nextEnd = endTime;
    if (endTime) {
      const [startHours, startMinutes] = newStartTime.split(":").map(Number);
      const [endHours, endMinutes] = endTime.split(":").map(Number);
      if (endHours * 60 + endMinutes <= startHours * 60 + startMinutes) {
        nextEnd = "";
      }
    }
    onChange({ startTime: newStartTime, endTime: nextEnd, hasOverlap: false });
  };

  const handleEndTimeChange = (event: SelectChangeEvent) => {
    if (timeLocked) return;
    onChange({ endTime: event.target.value, hasOverlap: false });
  };

  const hourlyPrice = getHourlyPrice(draft, lanes);
  const hasSelectedDate =
    (dateSelectionType === "single" && !!selectedDate) ||
    (dateSelectionType === "multiple" && selectedDates.length > 0) ||
    (dateSelectionType === "range" && !!dateRange.from && !!dateRange.to);

  return (
    <Box>
      {!isFirst && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.sameDay}
                onChange={(event) => onToggleSameDay?.(event.target.checked)}
              />
            }
            label="Same day"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.sameTime}
                onChange={(event) => onToggleSameTime?.(event.target.checked)}
              />
            }
            label="Same time"
          />
        </Stack>
      )}

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id={`lane-select-label-${draft.id}`}>Lane/Cage</InputLabel>
        <Select
          labelId={`lane-select-label-${draft.id}`}
          value={selectedLaneId}
          onChange={(event: SelectChangeEvent) =>
            onChange({ selectedLaneId: event.target.value, hasOverlap: false })
          }
          label="Lane/Cage"
          required
        >
          {lanes.map((lane) => (
            <MenuItem key={lane.id} value={lane.id}>
              {lane.name}
            </MenuItem>
          ))}
        </Select>
        {selectedLaneId && (
          <Box sx={{ mt: 1, mb: 2, display: "flex", alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Hourly rate:
            </Typography>
            <Typography variant="body1" fontWeight="medium" color="primary.dark">
              {formatCurrency(hourlyPrice)}
            </Typography>
          </Box>
        )}
      </FormControl>

      <Typography
        variant="subtitle1"
        sx={{ mb: 2, fontWeight: 600, display: "flex", alignItems: "center" }}
      >
        <EventIcon sx={{ mr: 1 }} />
        Select Date
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Date Selection Type:
        </Typography>
        <ToggleButtonGroup
          value={dateSelectionType}
          exclusive
          onChange={handleDateSelectionTypeChange}
          size="small"
          disabled={dateLocked}
          sx={{
            flexWrap: "wrap",
            "& .MuiToggleButton-root": {
              flex: { xs: "1 1 auto", sm: "none" },
            },
          }}
        >
          <ToggleButton value="single">Single Date</ToggleButton>
          <ToggleButton value="multiple">Multiple Dates</ToggleButton>
          <ToggleButton value="range">Date Range</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Paper
        elevation={2}
        sx={{
          p: { xs: 1, sm: 2 },
          mb: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          position: "relative",
          opacity: dateLocked ? 0.7 : 1,
          overflowX: "auto",
          "& .rdp": {
            "--rdp-cell-size": "36px",
            margin: 0,
          },
          "@media (max-width: 600px)": {
            "& .rdp": {
              "--rdp-cell-size": "32px",
            },
          },
        }}
      >
        {isCheckingAvailability && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 10,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        {dateSelectionType === "single" && (
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleSingleDateSelect}
            disabled={dateLocked ? true : [{ before: new Date() }]}
          />
        )}
        {dateSelectionType === "multiple" && (
          <DayPicker
            mode="multiple"
            selected={selectedDates}
            onSelect={handleMultipleDateSelectForPicker}
            disabled={dateLocked ? true : [{ before: new Date() }]}
          />
        )}
        {dateSelectionType === "range" && (
          <DayPicker
            mode="range"
            selected={dateRange}
            onSelect={handleDateRangeSelectForPicker}
            disabled={dateLocked ? true : [{ before: new Date() }]}
          />
        )}
      </Paper>

      {dateSelectionType === "single" && selectedDate && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Chip label={format(selectedDate, "PPP")} color="primary" variant="outlined" icon={<EventIcon />} />
        </Box>
      )}

      {dateSelectionType === "multiple" && selectedDates.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Selected Dates ({selectedDates.length}):
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
            {selectedDates.map((date) => (
              <Chip
                key={date.getTime()}
                label={format(date, "MMM d")}
                color="primary"
                size="small"
                variant="outlined"
                icon={<EventIcon />}
                onDelete={dateLocked ? undefined : () => handleRemoveDate(date)}
                deleteIcon={<CloseIcon />}
              />
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Time Selection:
          </Typography>
          <ToggleButtonGroup
            value={useIndividualTimes ? "individual" : "single"}
            exclusive
            onChange={(_event, newValue) => {
              if (timeLocked || newValue === null) return;
              onChange({ useIndividualTimes: newValue === "individual" });
            }}
            size="small"
            disabled={timeLocked}
            sx={{
              flexWrap: "wrap",
              "& .MuiToggleButton-root": {
                flex: { xs: "1 1 auto", sm: "none" },
              },
            }}
          >
            <ToggleButton value="single">Single Time for All Dates</ToggleButton>
            <ToggleButton value="individual">Individual Times per Date</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {dateSelectionType === "range" && dateRange.from && dateRange.to && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Chip
            label={`${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d, yyyy")}`}
            color="primary"
            variant="outlined"
            icon={<EventIcon />}
          />
        </Box>
      )}

      <Typography
        variant="subtitle1"
        sx={{ mb: 2, mt: 1, fontWeight: 600, display: "flex", alignItems: "center" }}
      >
        <AccessTimeIcon sx={{ mr: 1 }} />
        Select Time
      </Typography>

      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: hasOverlap ? "error.main" : "divider",
          position: "relative",
        }}
      >
        {!hasSelectedDate ? (
          <Alert severity="info">
            Please select{" "}
            {dateSelectionType === "single"
              ? "a date"
              : dateSelectionType === "multiple"
                ? "one or more dates"
                : "a date range"}{" "}
            first to see available time slots
          </Alert>
        ) : dateSelectionType === "multiple" && useIndividualTimes ? (
          <Box>
            {selectedDates.map((date) => {
              const dateKey = formatDateToYYYYMMDD(date);
              const dateTimes = individualDateTimes[dateKey] || { startTime: "", endTime: "" };
              return (
                <Paper key={dateKey} elevation={1} sx={{ p: 2, mb: 2, border: "1px solid", borderColor: "divider" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                    {format(date, "EEEE, MMMM d")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Start Time</InputLabel>
                        <Select
                          value={dateTimes.startTime}
                          label="Start Time"
                          disabled={timeLocked}
                          onChange={(event) =>
                            onChange({
                              individualDateTimes: {
                                ...individualDateTimes,
                                [dateKey]: { ...dateTimes, startTime: event.target.value },
                              },
                              hasOverlap: false,
                            })
                          }
                        >
                          {timeSlots.map((slot) => (
                            <MenuItem key={slot.value} value={slot.value}>
                              {slot.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>End Time</InputLabel>
                        <Select
                          value={dateTimes.endTime}
                          label="End Time"
                          disabled={timeLocked || !dateTimes.startTime}
                          onChange={(event) =>
                            onChange({
                              individualDateTimes: {
                                ...individualDateTimes,
                                [dateKey]: { ...dateTimes, endTime: event.target.value },
                              },
                              hasOverlap: false,
                            })
                          }
                        >
                          {timeSlots
                            .filter((slot) => {
                              if (!dateTimes.startTime) return false;
                              const [startHours, startMinutes] = dateTimes.startTime.split(":").map(Number);
                              const [slotHours, slotMinutes] = slot.value.split(":").map(Number);
                              return slotHours * 60 + slotMinutes > startHours * 60 + startMinutes;
                            })
                            .map((slot) => (
                              <MenuItem key={slot.value} value={slot.value}>
                                {slot.label}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 2 }} disabled={timeLocked}>
              <InputLabel>Start Time</InputLabel>
              <Select
                value={startTime}
                onChange={handleStartTimeChange}
                label="Start Time"
                error={hasOverlap}
                disabled={timeLocked}
              >
                {timeSlots.map((slot) => (
                  <MenuItem key={slot.value} value={slot.value}>
                    {slot.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={timeLocked || !startTime}>
              <InputLabel>End Time</InputLabel>
              <Select
                value={endTime}
                onChange={handleEndTimeChange}
                label="End Time"
                error={hasOverlap}
                disabled={timeLocked || !startTime}
              >
                {endTimeSlots.map((slot) => (
                  <MenuItem key={slot.value} value={slot.value}>
                    {slot.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {getNumberOfDays(draft) > 1 && startTime && endTime && (
              <Paper elevation={1} sx={{ p: 2, mt: 2, border: "1px solid", borderColor: "primary.light" }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", color: "primary.dark" }}>
                  Multiple Dates Summary ({getNumberOfDays(draft)} days)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Total Duration:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDuration(getTotalDuration(draft))}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Total Amount:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(calculateDraftSubtotal(draft, lanes, discountPercent))}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}
            {hasOverlap && (
              <Alert severity="error" sx={{ mt: 2 }}>
                This time slot overlaps with already booked slots. Please select a different time.
              </Alert>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AdminBookingDraftFields;
